import LogoHorizontal from '@/components/media/logo/logo-horizontal';
import { useSettingsContext } from '@/components/layout/settings';
import SvgColor from '@/components/media/svg-color';
import { ASSETS_API } from '../../config-global';
import { useOffSetTop } from '@/hooks/ui/use-off-set-top';
import { useResponsive } from '@/hooks/ui/use-responsive';
import { useScrollTrigger } from '@/hooks/ui/use-scroll-trigger';
import { bgBlur } from '@/theme/css';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { Slide, Tooltip } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import { useTranslation } from 'react-i18next';
import { AccountPopover, LanguagePopover } from '../_common';
import { NAV, HEADER } from '../config-layout';
import HelpPopover from '../_common/help-popover';

type Props = {
  onOpenNav?: VoidFunction;
};

export default function Header({ onOpenNav }: Props) {
  const { t } = useTranslation();
  const theme = useTheme();

  const settings = useSettingsContext();

  const isNavHorizontal = settings.themeLayout === 'horizontal';

  const isNavMini = settings.themeLayout === 'mini';

  const lgUp = useResponsive('up', 'lg');

  const offset = useOffSetTop(HEADER.H_DESKTOP);

  const offsetTop = offset && !isNavHorizontal;

  // Hook for hiding header on scroll down and showing on scroll up
  const hideOnScroll = useScrollTrigger({
    threshold: 200,
    disableHysteresis: false,
  });

  // Hook to detect when breadcrumb becomes sticky (same threshold as breadcrumb)
  const breadcrumbSticky = useScrollTrigger({
    threshold: 200,
    disableHysteresis: true,
  });

  const getHeaderShadow = () => {
    if (breadcrumbSticky) return 'none';
    if (theme.palette.mode === 'light') {
      return `0 2px 4px 0 ${theme.palette.divider}`;
    }
    return theme.customShadows?.z4 || theme.shadows[4];
  };

  const renderContent = (
    <>
      {!lgUp && (
        <>
          <IconButton
            onClick={onOpenNav}
            sx={{
              px: { xs: 0.5, sm: 1 },
              py: { xs: 0.5, sm: 1 },
              mx: { xs: 0.5, sm: 0.75 },
            }}
          >
            <SvgColor
              src={`${ASSETS_API}/assets/icons/navbar/ic_menu_item.svg`}
              sx={{ width: { xs: 20, sm: 24 }, height: { xs: 20, sm: 24 } }}
            />
          </IconButton>
          <LogoHorizontal
            sx={{
              ml: { xs: 1, sm: 1 },
              mr: { xs: 1, sm: 1 },
              display: 'flex',
              alignItems: 'center',
              maxWidth: { xs: 110, sm: 120, md: 130 },
              height: 'auto',
            }}
          />
        </>
      )}

      {lgUp && settings.themeLayout === 'horizontal' && (
        <LogoHorizontal
          sx={{
            mr: { md: 1, lg: 2 },
            ml: { md: 1, lg: 2 },
            display: 'flex',
            alignItems: 'center',
            maxWidth: { md: 130, lg: 140 },
            height: 'auto',
          }}
        />
      )}

      {/* <SearchBar /> */}

      <Stack
        flexGrow={1}
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={{ xs: 0.5, sm: 0.75, md: 1 }}
      >
        <HelpPopover />

        {/* <NotificationsPopover /> */}

        <LanguagePopover />

        {/* <ContactsPopover /> */}

        {/* <SettingsButton /> */}
        {/* onClick={colorMode.toggleColorMode} */}
        <Tooltip title={t('Theme mode')}>
          <IconButton
            sx={{ ml: 1 }}
            color="inherit"
            onClick={() =>
              settings.onUpdate('themeMode', settings.themeMode === 'dark' ? 'light' : 'dark')
            }
          >
            {settings.themeMode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
        </Tooltip>

        {/* <IconButton
          component={m.button}
          whileTap="tap"
          whileHover="hover"
          variants={varHover(1.05)}
          onClick={popover.onOpen}
          sx={{
            width: 40,
            height: 40,
            ...(popover.open && {
              bgcolor: 'action.selected',
            }),
          }}
        >
          <Iconify icon={locales.currentLang.icon} sx={{ borderRadius: 0.65, width: 28 }} />
        </IconButton> */}

        <AccountPopover />
      </Stack>
    </>
  );

  return (
    <Slide
      appear={false}
      direction="down"
      in={!hideOnScroll}
      timeout={theme.transitions.duration.shorter}
    >
      <AppBar
        sx={{
          height: HEADER.H_MOBILE,
          zIndex: hideOnScroll ? theme.zIndex.appBar - 3 : theme.zIndex.appBar + 1,
          boxShadow: getHeaderShadow(),
          ...(!breadcrumbSticky && {
            ...bgBlur({
              color: theme.palette.background.default,
            }),
          }),
          transition: theme.transitions.create(['height', 'transform', 'z-index', 'box-shadow'], {
            duration: theme.transitions.duration.shorter,
          }),
          ...(lgUp && {
            width: `calc(100% - ${NAV.W_VERTICAL + 1}px)`,
            height: HEADER.H_DESKTOP,
            ...(offsetTop && {
              height: HEADER.H_DESKTOP_OFFSET,
            }),
            ...(isNavHorizontal && {
              width: 1,
              bgcolor: 'background.default',
              height: HEADER.H_DESKTOP_OFFSET,
              borderBottom: `dashed 1px ${theme.palette.divider}`,
            }),
            ...(isNavMini && {
              width: `calc(100% - ${NAV.W_MINI + 1}px)`,
            }),
          }),
        }}
      >
        <Toolbar
          sx={{
            height: 1,
            px: { xs: 0, sm: 0, md: 1, lg: 1 },
            pt: 0,
            pb: { xs: 0, sm: 0 },
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {renderContent}
        </Toolbar>
      </AppBar>
    </Slide>
  );
}
